import { ExportFormat, ExportOption } from '@/modules/exports'
import type {
  ExportVehicleDocumentsMutation,
  ExportVehicleExpirationsMutation,
  ExportVehicleServicePlansMutation,
  VehicleListExportMutationVariables,
  VehicleListExportPayload,
} from '@/modules/vehicles'
import {
  EXPORT_VEHICLE_DOCUMENTS_MUTATION,
  EXPORT_VEHICLE_EXPIRATIONS_MUTATION,
  EXPORT_VEHICLE_SERVICE_PLANS_MUTATION,
} from '@/modules/vehicles/mutations'
import { useMutation } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useNavigate } from 'react-router'
import type { VehicleListExportFilters, VehicleListExportKind } from './types'

type ExportMutationData =
  | ExportVehicleDocumentsMutation
  | ExportVehicleExpirationsMutation
  | ExportVehicleServicePlansMutation

const MUTATIONS = {
  documents: EXPORT_VEHICLE_DOCUMENTS_MUTATION,
  expirations: EXPORT_VEHICLE_EXPIRATIONS_MUTATION,
  services: EXPORT_VEHICLE_SERVICE_PLANS_MUTATION,
} satisfies Record<VehicleListExportKind, unknown>

/**
 * The three mutations return the same payload under three different field
 * names, and nothing downstream cares which one it came from.
 */
function getPayload(data: ExportMutationData): VehicleListExportPayload {
  if ('exportVehicleDocuments' in data) return data.exportVehicleDocuments
  if ('exportVehicleExpirations' in data) return data.exportVehicleExpirations
  return data.exportVehicleServicePlans
}

/**
 * Starts an export of one of the fleet-wide vehicle lists.
 *
 * A PDF is rendered inline and opened in the print view; anything else is
 * queued and picked up under "My exports" — the same split the equipment
 * export uses, so an export behaves the same wherever it is started.
 */
export function useVehicleListExport<TKind extends VehicleListExportKind>(
  kind: TKind
) {
  const translations = useTranslate({
    error: 'error.generic-server-error',
    queued: 'exports.success-user-exports',
    exportTimeout: 'exports.export-timeout',
  })

  const addToast = useToast()
  const navigate = useNavigate()

  const [runExport, { loading }] = useMutation<
    ExportMutationData,
    VehicleListExportMutationVariables<VehicleListExportFilters[TKind]>
  >(MUTATIONS[kind], {
    onCompleted(data) {
      const result = getPayload(data)

      if (result?.exportTimeout) {
        addToast('info', translations.exportTimeout)
      } else if (result?.html) {
        void navigate('/export', { state: { html: result.html } })
      } else if (result?.ok) {
        addToast('success', translations.queued)
      } else {
        addToast('error', translations.error)
      }
    },
    onError() {
      addToast('error', translations.error)
    },
  })

  async function exportList(
    exportFormat: ExportFormat,
    filters: VehicleListExportFilters[TKind]
  ) {
    await runExport({
      variables: {
        exportFormat,
        // A PDF comes straight back as a rendered report; an Excel workbook is
        // built in the background and saved for the user to download.
        exportOption:
          exportFormat === ExportFormat.PDF
            ? ExportOption.DOWNLOAD
            : ExportOption.SAVE,
        filters,
      },
    })
  }

  return { exportList, loading }
}
